import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { filterAndSortMenuListItems } from './menuListSearch';

import type { MenuListItemData, MenuListOptionItemData } from './types';

/**
 * 가상 포커스 트리거 요소 식별용 data attribute
 */
export const MENU_LIST_TRIGGER_ATTR = 'data-menu-list-trigger';
export const MENU_LIST_ITEM_KEY_ATTR = 'data-menu-item-key';

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * 메뉴 아이템의 키를 생성합니다.
 * @description
 * - 일반 아이템: `${itemIndex}` (예: "0", "1")
 * - 섹션 내 아이템: `${sectionIndex}-${childIndex}` (예: "0-0", "0-1")
 * @param itemIndex 아이템 또는 섹션의 인덱스
 * @param childIndex 섹션 내 자식 아이템의 인덱스 (섹션 내 아이템일 경우)
 * @example
 * getMenuItemKey(0) // "0"
 * getMenuItemKey(1, 2) // "1-2"
 */
export const getMenuItemKey = (itemIndex: number, childIndex?: number): string =>
  childIndex !== undefined ? `${itemIndex}-${childIndex}` : `${itemIndex}`;

/**
 * 첫 번째 selected 아이템의 키를 반환합니다.
 * @description
 * - 섹션 내 아이템도 검색합니다.
 * - selected 아이템이 없으면 undefined를 반환합니다.
 * @param items 메뉴 아이템 목록
 * @example
 * const key = getFirstSelectedItemKey(items);
 * useMenuListNavigation({ items, initialFocusedKey: key });
 */
export const getFirstSelectedItemKey = (items: readonly MenuListItemData[]): string | undefined => {
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (item.type === 'section') {
      const sectionItems = item.items ?? [];
      for (let j = 0; j < sectionItems.length; j++) {
        if (sectionItems[j].selected) {
          return getMenuItemKey(i, j);
        }
      }
    } else if ((item as MenuListOptionItemData).selected) {
      return getMenuItemKey(i);
    }
  }
  return undefined;
};

// ============================================================================
// Types
// ============================================================================

export interface UseMenuListNavigationOptions {
  /**
   * 메뉴 아이템 목록
   */
  items: readonly MenuListItemData[];
  /**
   * items 뒤에 추가할 포커스 가능한 키 목록
   * @description
   * - MenuList 외부 요소(예: Footer 버튼)에 가상 포커스 지정 시 사용
   * - 해당 키를 가진 요소는 containerRef 내에 위치해야 함
   */
  appendFocusableKeys?: readonly string[];
  /**
   * 섹션 접기/펼치기 기능 사용 여부
   */
  expandable?: boolean;
  /**
   * 검색 텍스트에 따라 메뉴 항목을 필터링하는 함수입니다.
   * 렌더링된 메뉴 목록과 키보드 탐색 목록의 기준을 맞추기 위해 사용합니다.
   */
  filterItems?: (
    items: readonly MenuListItemData[],
    searchText: string,
  ) => readonly MenuListItemData[];
  /**
   * 키보드 탐색 시작 시 포커스할 아이템의 키
   * @description
   * - 지정 시, 첫 키보드 탐색에서 이 키부터 시작합니다.
   * - 키가 유효하지 않으면 첫 번째 아이템부터 시작합니다.
   * - 예: Select에서 선택된 항목을 지정하면 해당 항목부터 탐색 시작
   */
  initialFocusedKey?: string;
  /**
   * items 앞에 추가할 포커스 가능한 키 목록
   * @description
   * - MenuList 외부 요소(예: Toolbar의 selectAll Checkbox)에 가상 포커스 지정 시 사용
   * - 해당 키를 가진 요소는 containerRef 내에 위치해야 함
   */
  prependFocusableKeys?: readonly string[];
  /**
   * 검색 텍스트입니다.
   */
  searchText?: string;
  /**
   * 접힌 섹션들의 키 Set
   */
  sectionFolded?: Set<string>;
  /**
   * `true`일 때, 키보드 탐색 시에만 가상 포커스를 표시합니다.
   * @description
   * - 마우스 호버 시에도 focusedItemKey는 업데이트되지만 시각적 포커스는 숨겨집니다.
   * - 키보드로 탐색을 시작하면 다시 표시됩니다.
   * @default true
   */
  showVirtualFocusOnKeyboardOnly?: boolean;
}

export interface MenuListNavigationApi {
  /**
   * 현재 포커스된 아이템의 키입니다. 포커스된 아이템이 없으면 null입니다.
   */
  focusedItemKey: string | null;
  /**
   * 다음 아이템으로 포커스를 이동합니다.
   * @returns 이동 후 포커스된 아이템의 키
   */
  navigateDown: () => string | null;
  /**
   * 이전 아이템으로 포커스를 이동합니다.
   * @returns 이동 후 포커스된 아이템의 키
   */
  navigateUp: () => string | null;
  /**
   * 첫 번째 포커스 가능한 아이템으로 포커스를 초기화합니다.
   */
  resetToFirst: () => void;
  /**
   * 현재 포커스된 아이템을 선택(클릭)합니다.
   * @param options.keepVirtualFocus true이면 선택 후에도 가상 포커스를 유지합니다.
   * @returns 선택 성공 여부
   */
  selectFocused: (options?: { keepVirtualFocus?: boolean }) => boolean;
  /**
   * 특정 키의 아이템으로 포커스를 설정합니다.
   * @param key 포커스할 아이템의 키
   */
  setFocusedKey: (key: string | null) => void;
}

export interface UseMenuListNavigationReturn extends MenuListNavigationApi {
  /**
   * 포커스 가능한 아이템 키 목록
   */
  focusableItemKeys: string[];
  /**
   * 컨테이너 요소에 적용할 props를 반환하는 함수 (Props Getter 패턴)
   * @description
   * ref, role, aria-activedescendant를 포함합니다.
   * 외부 ref가 필요한 경우 props로 전달하면 내부 ref와 merge됩니다.
   * @param props 외부 ref를 포함한 추가 props
   * @example
   * // ref 불필요 시
   * <MenuContainerRoot {...navigation.getContainerProps()}>
   * // 외부 ref 필요 시
   * <MenuContainerRoot {...navigation.getContainerProps({ ref: myRef })}>
   */
  getContainerProps: <E extends HTMLElement = HTMLElement>(props?: {
    ref?: React.Ref<E>;
  }) => {
    'aria-activedescendant': string | undefined;
    ref: (node: E | null) => void;
    role: 'listbox';
  };
  /**
   * 아이템 요소에 적용할 props를 반환하는 함수 (Props Getter 패턴)
   * @description
   * data-menu-item-key, aria-selected, onMouseEnter를 포함합니다.
   * @param itemKey 아이템의 고유 키
   * @example
   * <MenuOptionItem {...navigation.getItemProps('item-0')} />
   */
  getItemProps: (itemKey: string) => {
    'aria-selected': boolean;
    'data-menu-item-key': string;
    onMouseEnter: () => void;
  };
  // ========== Props Getter API ==========
  /**
   * 트리거 요소에 적용할 props를 반환하는 함수 (Props Getter 패턴)
   * @description
   * onFocus, onBlur, data-menu-list-trigger 속성을 포함합니다.
   * 이 props를 적용한 요소 간 포커스 이동 시 가상 포커스가 유지됩니다.
   * @example
   * <SearchInput {...navigation.getTriggerProps()} />
   * <MenuList {...navigation.getTriggerProps()} />
   */
  getTriggerProps: () => {
    'data-menu-list-trigger': '';
    onBlur: (event: React.FocusEvent<HTMLElement>) => void;
    onFocus: (event: React.FocusEvent<HTMLElement>) => void;
  };
  /**
   * 아이템 클릭 시 호출할 핸들러 (해당 아이템으로 포커스 설정)
   */
  handleItemClick: (event: React.MouseEvent, itemKey: string) => void;
  /**
   * 메뉴 리스트의 onKeyDown 핸들러
   */
  handleKeyDown: (event: React.KeyboardEvent<HTMLUListElement>) => void;
  /**
   * 특정 아이템이 포커스되어 있는지 확인하는 함수
   * 트리거에 포커스가 없으면 항상 false를 반환합니다.
   */
  isItemFocused: (itemKey: string) => boolean;
}

// ============================================================================
// Hook Implementation
// ============================================================================

export function useMenuListNavigation(
  options: UseMenuListNavigationOptions,
): UseMenuListNavigationReturn {
  const {
    items,
    prependFocusableKeys,
    appendFocusableKeys,
    expandable,
    filterItems: filterItemsProp,
    initialFocusedKey,
    searchText = '',
    sectionFolded,
    showVirtualFocusOnKeyboardOnly = true,
  } = options;

  // ========== 내부 컨테이너 ref ==========
  const containerRef = useRef<HTMLElement | null>(null);

  // ========== 가상 포커스 상태 관리 ==========
  const [focusedItemKey, setFocusedItemKey] = useState<string | null>(null);
  const [isListFocused, setIsListFocused] = useState(false);
  const prevSearchTextRef = useRef(searchText);
  /**
   * 키보드로 탐색 중인지 여부
   * @description
   * - showVirtualFocusOnKeyboardOnly가 true일 때만 사용됩니다.
   * - 키보드 탐색 시 true, 마우스 이벤트 시 false로 설정됩니다.
   */
  const [isKeyboardNavigating, setIsKeyboardNavigating] = useState(false);

  // ========== Items 필터링·정렬 (searchText 기반, MenuList와 동일 로직) ==========
  const filteredItems = useMemo(() => {
    if (!searchText) return items;
    if (filterItemsProp) return filterItemsProp(items, searchText);
    return filterAndSortMenuListItems(items, searchText);
  }, [filterItemsProp, items, searchText]);

  // ========== 포커스 가능한 아이템 키 목록 계산 ==========
  const getItemsFocusableKeys = useCallback((): string[] => {
    const keys: string[] = [];

    filteredItems.forEach((item, itemIndex) => {
      if (item.type === 'section') {
        // 섹션 헤더가 expandable이면 포커스 가능
        const itemExpandable = item.expandable ?? expandable;
        if (item.title && itemExpandable) {
          keys.push(`section-${itemIndex}`);
        }

        // 섹션이 펼쳐져 있으면 자식 아이템도 포커스 가능
        const isExpanded = !sectionFolded?.has(`${itemIndex}`);
        if (isExpanded) {
          (item.items ?? []).forEach((child: MenuListOptionItemData, childIndex: number) => {
            if (!child.disabled && (child.interactive ?? true)) {
              keys.push(getMenuItemKey(itemIndex, childIndex));
            }
          });
        }
      } else {
        // 일반 옵션인 경우
        if (!item.disabled && (item.interactive ?? true)) {
          keys.push(getMenuItemKey(itemIndex));
        }
      }
    });

    return keys;
  }, [filteredItems, expandable, sectionFolded]);

  /**
   * 전체 포커스 가능 키 목록 반환
   * @description prepend → items 기반 → append 순서로 결합
   */
  const getFocusableItemKeys = useCallback((): string[] => {
    const itemsKeys = getItemsFocusableKeys();
    return [...(prependFocusableKeys ?? []), ...itemsKeys, ...(appendFocusableKeys ?? [])];
  }, [getItemsFocusableKeys, prependFocusableKeys, appendFocusableKeys]);

  const focusableItemKeys = getFocusableItemKeys();

  // ========== 첫 번째 포커스 가능한 아이템 키 ==========
  const getFirstFocusableKey = useCallback((): string | null => {
    const keys = getFocusableItemKeys();
    return keys.length > 0 ? keys[0] : null;
  }, [getFocusableItemKeys]);

  const pendingInitialKeyRef = useRef<string | null>(null);

  const computeInitialFocusableKey = useCallback((): string | null => {
    if (initialFocusedKey) {
      const keys = getFocusableItemKeys();
      if (keys.includes(initialFocusedKey)) {
        return initialFocusedKey;
      }
    }

    return null;
  }, [getFocusableItemKeys, initialFocusedKey]);

  // ========== 아이템 변경 시 포커스 유효성 검사 ==========
  useEffect(() => {
    const keys = getFocusableItemKeys();
    if (focusedItemKey) {
      // 현재 포커스된 키가 더 이상 유효하지 않으면 포커스 초기화
      if (!keys.includes(focusedItemKey)) {
        setFocusedItemKey(null);
      }
    }

    if (focusedItemKey === null) {
      pendingInitialKeyRef.current = computeInitialFocusableKey();
    } else {
      pendingInitialKeyRef.current = null;
    }
  }, [computeInitialFocusableKey, focusedItemKey, getFocusableItemKeys]);

  // ========== searchText 변경 시 포커스 초기화 ==========
  useEffect(() => {
    if (prevSearchTextRef.current !== searchText) {
      setFocusedItemKey(null);
      prevSearchTextRef.current = searchText;
    }
  }, [searchText]);

  // ========== 스크롤 헬퍼 ==========
  const scrollToItem = useCallback(
    (itemKey: string) => {
      const container = containerRef.current;
      const element = container?.querySelector(`[${MENU_LIST_ITEM_KEY_ATTR}="${itemKey}"]`);
      if (element instanceof HTMLElement) {
        element.scrollIntoView({ block: 'nearest' });
      }
    },
    [containerRef],
  );

  // ========== 네비게이션 함수 ==========
  const navigate = useCallback(
    (direction: 'up' | 'down'): string | null => {
      const keys = getFocusableItemKeys();
      if (keys.length === 0) return null;

      // 키보드 탐색 모드 활성화 (showVirtualFocusOnKeyboardOnly 옵션용)
      setIsKeyboardNavigating(true);

      if (focusedItemKey === null && pendingInitialKeyRef.current) {
        const pendingKey = pendingInitialKeyRef.current;
        pendingInitialKeyRef.current = null;
        if (pendingKey && keys.includes(pendingKey)) {
          setFocusedItemKey(pendingKey);
          scrollToItem(pendingKey);
          return pendingKey;
        }
      }

      // focusedItemKey가 없을 때 initialFocusedKey 또는 -1에서 시작
      let currentIndex: number;
      if (focusedItemKey) {
        currentIndex = keys.indexOf(focusedItemKey);
      } else {
        currentIndex = -1;
      }

      let nextIndex = currentIndex + (direction === 'up' ? -1 : 1);

      // 순환 처리
      if (nextIndex < 0) nextIndex = keys.length - 1;
      if (nextIndex >= keys.length) nextIndex = 0;

      const nextKey = keys[nextIndex];
      setFocusedItemKey(nextKey);

      // 실제 DOM 포커스를 트리거 요소로 이동 (클릭된 아이템에서 포커스 제거)
      // 이렇게 해야 가상 포커스만 표시됨
      const activeElement = document.activeElement as HTMLElement | null;
      const isActiveInTrigger = activeElement?.hasAttribute(MENU_LIST_TRIGGER_ATTR);
      if (!isActiveInTrigger && activeElement) {
        // 현재 포커스된 요소의 가장 가까운 트리거 조상을 찾음
        // (예: li → ul[data-menu-list-trigger])
        const closestTrigger = activeElement.closest(`[${MENU_LIST_TRIGGER_ATTR}]`);
        if (closestTrigger instanceof HTMLElement) {
          closestTrigger.focus();
        }
      }

      scrollToItem(nextKey);

      return nextKey;
    },
    [focusedItemKey, getFocusableItemKeys, scrollToItem],
  );

  // ========== 현재 포커스된 아이템 선택 ==========
  const selectFocused = useCallback(
    (options?: { keepVirtualFocus?: boolean }): boolean => {
      if (!focusedItemKey) return false;

      const focusedElement = containerRef.current?.querySelector(
        `[${MENU_LIST_ITEM_KEY_ATTR}="${focusedItemKey}"]`,
      );
      if (focusedElement instanceof HTMLElement) {
        // keepVirtualFocus가 true이면 키보드 탐색 모드 유지
        if (options?.keepVirtualFocus) {
          setIsKeyboardNavigating(true);
        }
        focusedElement.click();
        return true;
      }
      return false;
    },
    [focusedItemKey],
  );

  // ========== 첫 번째 아이템으로 포커스 초기화 ==========
  const resetToFirst = useCallback((): void => {
    const firstKey = getFirstFocusableKey();
    setFocusedItemKey(firstKey);
  }, [getFirstFocusableKey]);

  // ========== 아이템 클릭 시 해당 아이템으로 포커스 설정 ==========
  const handleItemClick = useCallback((event: React.MouseEvent, itemKey: string): void => {
    setFocusedItemKey(itemKey);
    if (event.nativeEvent instanceof PointerEvent && event.nativeEvent.pointerType !== '') {
      // 마우스 클릭 시 키보드 탐색 모드 비활성화
      setIsKeyboardNavigating(false);
    }
  }, []);

  // ========== 트리거 focus 핸들러 ==========
  const handleTriggerFocus = useCallback(
    (_event: React.FocusEvent<HTMLElement>): void => {
      setIsListFocused(true);

      if (focusedItemKey === null) {
        pendingInitialKeyRef.current = computeInitialFocusableKey();
      }
    },
    [computeInitialFocusableKey, focusedItemKey],
  );

  // ========== 트리거 blur 핸들러 ==========
  const handleTriggerBlur = useCallback(
    (event: React.FocusEvent<HTMLElement>): void => {
      // relatedTarget: 다음에 포커스될 요소
      // 다음 포커스 대상이 같은 컨테이너 내 트리거 요소면 가상 포커스 유지
      const nextFocusingTarget = event.relatedTarget as HTMLElement | null;
      if (nextFocusingTarget?.hasAttribute(MENU_LIST_TRIGGER_ATTR)) {
        const isWithinContainer = containerRef.current?.contains(nextFocusingTarget);
        if (isWithinContainer) {
          return;
        }
      }
      setIsListFocused(false);
    },
    [containerRef],
  );

  // ========== 키보드 이벤트 핸들러 ==========
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLUListElement>) => {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          navigate('down');
          break;
        case 'ArrowUp':
          event.preventDefault();
          navigate('up');
          break;
        case 'Enter':
        case ' ':
          event.preventDefault();
          selectFocused({ keepVirtualFocus: true });
          break;
        case 'Home':
          event.preventDefault();
          resetToFirst();
          break;
        case 'End': {
          event.preventDefault();
          const keys = getFocusableItemKeys();
          if (keys.length > 0) {
            setFocusedItemKey(keys[keys.length - 1]);
          }
          break;
        }
      }
    },
    [navigate, selectFocused, resetToFirst, getFocusableItemKeys],
  );

  // ========== 특정 아이템이 포커스되었는지 확인 ==========
  const isItemFocused = useCallback(
    (itemKey: string): boolean => {
      if (!isListFocused || focusedItemKey !== itemKey) return false;
      // showVirtualFocusOnKeyboardOnly가 true면 키보드 탐색 중일 때만 true 반환
      if (showVirtualFocusOnKeyboardOnly && !isKeyboardNavigating) return false;
      return true;
    },
    [isListFocused, focusedItemKey, showVirtualFocusOnKeyboardOnly, isKeyboardNavigating],
  );

  // ========== 트리거 props ==========
  const triggerProps = useMemo(
    () => ({
      [MENU_LIST_TRIGGER_ATTR]: '' as const,
      onBlur: handleTriggerBlur,
      onFocus: handleTriggerFocus,
    }),
    [handleTriggerBlur, handleTriggerFocus],
  );

  // ========== Props Getter API ==========
  const getTriggerProps = useCallback(() => triggerProps, [triggerProps]);

  const getItemProps = useCallback(
    (itemKey: string) => ({
      [MENU_LIST_ITEM_KEY_ATTR]: itemKey,
      'aria-selected': isItemFocused(itemKey),
      onMouseEnter: () => {
        if (isListFocused) {
          setFocusedItemKey(itemKey);
          // 마우스 호버 시 키보드 탐색 모드 비활성화
          setIsKeyboardNavigating(false);
        }
      },
    }),
    [isListFocused, isItemFocused],
  );

  const getContainerProps = useCallback(
    <E extends HTMLElement = HTMLElement>(props?: { ref?: React.Ref<E> }) => {
      const mergedRef = (node: E | null) => {
        // internal ref 설정
        containerRef.current = node;
        // external ref 설정
        if (props?.ref) {
          if (typeof props.ref === 'function') {
            props.ref(node);
          } else {
            props.ref.current = node;
          }
        }
      };

      return {
        ref: mergedRef,
        role: 'listbox' as const,
        'aria-activedescendant': focusedItemKey ?? undefined,
      };
    },
    [focusedItemKey],
  );

  return {
    // State
    focusedItemKey,
    focusableItemKeys,
    // Navigation API
    navigateUp: () => navigate('up'),
    navigateDown: () => navigate('down'),
    selectFocused,
    setFocusedKey: setFocusedItemKey,
    resetToFirst,
    // Event handlers
    handleItemClick,
    handleKeyDown,
    // Utilities
    isItemFocused,
    // Props Getter API
    getTriggerProps,
    getItemProps,
    getContainerProps,
  };
}
